import BouncingLoader from 'components/BouncingLoader/BouncingLoader';
import { ChartBuilder, ChartContext } from 'components/Charting/ChartBuilder';
import { isEmptyRecordOfArrays } from 'components/Charting/utils';
import ChartDataErrorScreen from 'components/ErrorStates/ChartErrorStates/ChartDataErrorScreen';
import ChartNoDataScreen from 'components/ErrorStates/ChartErrorStates/ChartNoDataScreen';
import { useRerenderAtLeastOnce } from 'hooks/useRerenderAtLeastOnce';
import throttle from 'lodash/throttle';
import { MutableRefObject, useEffect, useRef } from 'react';
import styled from 'styled-components';

const GraphLoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  backdrop-filter: blur(25px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GraphLoadingPlaceholder = () => (
  <GraphLoadingContainer>
    <BouncingLoader />
  </GraphLoadingContainer>
);

export const useChart = <
  TDataContext,
  TContext extends ChartContext<TDataContext>,
>(
  config: TContext['config'],
  builder: ChartBuilder<TDataContext, TContext>,
  dataContext: TDataContext | undefined,
): {
  chartRef: MutableRefObject<HTMLDivElement | null>;
  ErrorPlaceholder: React.ElementType;
} => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const context = useRef<TContext | undefined>(undefined);

  useRerenderAtLeastOnce();

  if (chartRef.current && !context.current) {
    context.current = builder(config, chartRef.current);
  }

  useEffect(() => {
    // throttle the request to fire the redraw not more than once in 200ms,
    // to avoid redraw on every pixel movement.
    const redraw = throttle(() => {
      context.current?.update(dataContext, true);
    }, 200);

    window.addEventListener('resize', redraw);

    return () => {
      window.removeEventListener('resize', redraw);
    };
  }, [dataContext]);

  context.current?.update(dataContext);

  const Placeholder = () => {
    if (dataContext) {
      const loading = (dataContext as any)?.loading;
      const hasNoData = isEmptyRecordOfArrays((dataContext as any).data);
      const hasErrors = !!(dataContext as any)?.errors?.length;

      if (loading) {
        return <GraphLoadingPlaceholder />;
      }

      if (hasErrors) {
        return <ChartDataErrorScreen />;
      }

      if (hasNoData) {
        return <ChartNoDataScreen />;
      }
    }

    return null;
  };

  return { chartRef, ErrorPlaceholder: Placeholder };
};
